import { useQuery } from '@tanstack/react-query';
import type { TenantFeaturesResponse } from '@up2d8/contracts';
import { useSession } from './session';

/**
 * De featureflags van het eigen bedrijf (Extra 6, punt 1).
 *
 * **Waarom dit naast de rechten bestaat.** Een module die uit staat, registreert zijn rechten niet,
 * dus `session.has('agenda.item.read')` is dan al `false` — dat deel van de vraag was dus al te
 * beantwoorden. Maar een module kán aanstaan terwijl een bedrijf de functie uitzette: `documents`
 * draait, en de tab Documenten hoort toch weg omdat `documents.library` uit staat. Dat verschil
 * zit alleen in de vlaggen.
 *
 * Een uur gecachet, net als het thema: een vlag wijzigt een paar keer per jaar, en elke
 * paginawissel opnieuw vragen is verkeer voor niets.
 *
 * **De standaard is "aan" bij twijfel.** Is het antwoord er nog niet, of mag deze gebruiker de
 * vlaggen niet opvragen, dan blijft een tab staan. Een lege tab is vervelend; een tab die
 * verdwijnt terwijl de functie gewoon werkt, laat iemand zoeken naar iets wat er wél is.
 */
export function useTenantFeatures(): { isOn: (flagKey: string) => boolean } {
  const { api, has } = useSession();

  const features = useQuery({
    queryKey: ['tenant-features'],
    enabled: has('tenants.tenant.view'),
    staleTime: 60 * 60 * 1000,
    retry: false,
    queryFn: async (): Promise<Record<string, boolean>> => {
      const { data, error } = await api.GET('/api/v1/tenant/features');
      if (error || !data) return {};
      return (data as TenantFeaturesResponse).features;
    },
  });

  return {
    isOn: (flagKey: string) => features.data?.[flagKey] ?? true,
  };
}
